import { datadogRum } from '@datadog/browser-rum';
import type { AnswerState } from '@repo/contracts/cardfinder/funnel/types';
import { OfferSchema } from '@repo/contracts/marketplace/offers/schema';
import { getGraphQLClient } from '@repo/platform-data-access/graphql-client';
import { env } from '@repo/platform-env';
import type {
  BalanceTransferRecommendationsQuery,
  BuildingCreditRecommendationsQuery,
  CashbackRecommendationsQuery,
  SavingOnInterestRecommendationsQuery,
  TravelRecommendationsQuery,
} from '@repo/supergraph-types/graphql';
import {
  BALANCE_TRANSFER_RECOMMENDATIONS_QUERY,
  BUILDING_CREDIT_RECOMMENDATIONS_QUERY,
  CASHBACK_RECOMMENDATIONS_QUERY,
  SAVING_ON_INTEREST_RECOMMENDATIONS_QUERY,
  TRAVEL_RECOMMENDATIONS_QUERY,
} from '@repo/supergraph-types/queries';
import * as v from 'valibot';
import type { BranchId } from '@/shared/config/funnel/constants';
import {
  toBalanceTransferInput,
  toBuildingCreditInput,
  toCashbackInput,
  toSavingOnInterestInput,
  toTravelInput,
} from './branchInputMapper';
import { joinCategoriesToOffers } from './schema';
import type { RecommendationResponse, ResultsRecommendationResponse } from './types';

// The shared GraphQL client discards parsed GraphQL error details (`flatErrors`)
// unless given a logger, so route them to Datadog rather than losing them silently.
const graphQLClientLogger = {
  error: (bindings: Record<string, unknown>, message: string) => {
    datadogRum.addError(new Error(message), bindings);
  },
};

type RecommendationsResponseUnion =
  | TravelRecommendationsQuery['travelRecommendations']
  | CashbackRecommendationsQuery['cashbackRecommendations']
  | BalanceTransferRecommendationsQuery['balanceTransferRecommendations']
  | SavingOnInterestRecommendationsQuery['savingOnInterestRecommendations']
  | BuildingCreditRecommendationsQuery['buildingCreditRecommendations'];

type RecommendationsResult = Extract<
  RecommendationsResponseUnion,
  { __typename: 'RecommendationsResult' }
>;

function toParsedOffers(offers: RecommendationsResult['offers']) {
  return offers.map(scoredOffer => ({
    ...scoredOffer,
    offer: v.parse(OfferSchema, scoredOffer.offer),
  }));
}

const BRANCH_QUERY_MAP = {
  travel: {
    query: TRAVEL_RECOMMENDATIONS_QUERY,
    toInput: toTravelInput,
    field: 'travelRecommendations' as const,
  },
  cash_back: {
    query: CASHBACK_RECOMMENDATIONS_QUERY,
    toInput: toCashbackInput,
    field: 'cashbackRecommendations' as const,
  },
  balance_transfer: {
    query: BALANCE_TRANSFER_RECOMMENDATIONS_QUERY,
    toInput: toBalanceTransferInput,
    field: 'balanceTransferRecommendations' as const,
  },
  saving_interest: {
    query: SAVING_ON_INTEREST_RECOMMENDATIONS_QUERY,
    toInput: toSavingOnInterestInput,
    field: 'savingOnInterestRecommendations' as const,
  },
  building_credit: {
    query: BUILDING_CREDIT_RECOMMENDATIONS_QUERY,
    toInput: toBuildingCreditInput,
    field: 'buildingCreditRecommendations' as const,
  },
};

export async function fetchRecommendations(
  branchId: BranchId,
  answers: AnswerState,
  isResultsPage = false,
): Promise<RecommendationResponse | ResultsRecommendationResponse | null> {
  const { query, toInput, field } = BRANCH_QUERY_MAP[branchId];

  try {
    const client = getGraphQLClient({
      url: env.NEXT_PUBLIC_SUPERGRAPH_URL,
      platform: 'web',
      logger: graphQLClientLogger,
    });
    const response = await client.query<{
      data: Record<typeof field, RecommendationsResponseUnion>;
    }>(
      query,
      {
        input: toInput(answers),
      },
      // This call needs no auth cookie (every input comes from funnel answers),
      // and the default `credentials: 'include'` attaches the browser's entire
      // cookie jar to this public-gateway request — large enough for a real
      // authenticated user to trip the gateway's request-size limit (413).
      { credentials: 'omit' },
    );

    const result = response.data[field];

    if (result.__typename === 'RecommendationsError') {
      datadogRum.addError(new Error(`Recommendations error: ${result.code}`), {
        message: result.message,
        branchId,
        field,
      });
      return null;
    }

    const parsedOffers = toParsedOffers(result.offers);

    if (isResultsPage) {
      return joinCategoriesToOffers({ offers: parsedOffers, categories: result.categories });
    }
    return { offers: parsedOffers };
  } catch (error) {
    datadogRum.addError(
      error instanceof Error ? error : new Error('Recommendations request failed'),
      {
        branchId,
      },
    );
    return null;
  }
}
